import type { AccountInfo } from "@azure/msal-browser";
import { environment } from "@/lib/environment";
import { loadTeamsSdk, resolveAuthHost } from "@/lib/teams/host";

/** Refresh a little before the real expiry so in-flight requests never race it. */
const EXPIRY_SKEW_MS = 5 * 60 * 1000;

interface JwtClaims {
	readonly aud?: string;
	readonly exp?: number;
	readonly name?: string;
	readonly oid?: string;
	readonly preferred_username?: string;
	readonly tid?: string;
	readonly upn?: string;
}

interface CachedToken {
	readonly expiresAt: number;
	readonly token: string;
}

interface TeamsSsoAuthResponse {
	readonly token?: unknown;
}

let apiTokenCache: CachedToken | null = null;
let graphTokenCache: CachedToken | null = null;
let ssoActive = false;

export const decodeJwtClaims = (token: string): JwtClaims | null => {
	const payload = token.split(".")[1];
	if (!payload) {
		return null;
	}

	try {
		const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
		const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
		const json = decodeURIComponent(
			atob(padded)
				.split("")
				.map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
				.join("")
		);
		return JSON.parse(json) as JwtClaims;
	} catch {
		return null;
	}
};

const toCachedToken = (token: string): CachedToken => {
	const claims = decodeJwtClaims(token);
	const expiresAt = claims?.exp ? claims.exp * 1000 - EXPIRY_SKEW_MS : Date.now() + EXPIRY_SKEW_MS;
	return { token, expiresAt };
};

const readCache = (cache: CachedToken | null): string | null =>
	cache && cache.expiresAt > Date.now() ? cache.token : null;

/**
 * Teams tab SSO: the host mints a token for our exposed API without any iframe
 * or popup. Fails when the user/admin has not consented yet, which is exactly
 * when the /teams/login button flow takes over.
 */
export const acquireTeamsApiToken = async (): Promise<string | null> => {
	const cached = readCache(apiTokenCache);
	if (cached) {
		return cached;
	}

	if ((await resolveAuthHost()) !== "teams") {
		return null;
	}

	try {
		const teams = await loadTeamsSdk();
		const token = await teams.authentication.getAuthToken();
		if (!token) {
			return null;
		}
		apiTokenCache = toCachedToken(token);
		ssoActive = true;
		return token;
	} catch (error: unknown) {
		console.debug("Teams getAuthToken failed", error);
		return null;
	}
};

/**
 * The Teams token targets our API, not Graph. TeamsSSO_Auth exchanges it
 * on-behalf-of for a Graph access token.
 */
export const acquireTeamsGraphToken = async (): Promise<string | null> => {
	const cached = readCache(graphTokenCache);
	if (cached) {
		return cached;
	}

	const ssoToken = await acquireTeamsApiToken();
	if (!ssoToken) {
		return null;
	}

	try {
		const response = await fetch(`${environment.VITE_API_URL}/TeamsSSO_Auth`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ token: ssoToken }),
		});

		if (!response.ok) {
			console.debug("TeamsSSO_Auth exchange failed", response.status);
			return null;
		}

		const payload = (await response.json()) as TeamsSsoAuthResponse;
		if (typeof payload.token !== "string" || !payload.token) {
			return null;
		}

		graphTokenCache = toCachedToken(payload.token);
		return payload.token;
	} catch (error: unknown) {
		console.debug("TeamsSSO_Auth exchange errored", error);
		return null;
	}
};

/** True once getAuthToken has succeeded, so token lookups skip the empty MSAL cache. */
export const isTeamsSsoActive = (): boolean => ssoActive;

export const clearTeamsSsoTokens = (): void => {
	apiTokenCache = null;
	graphTokenCache = null;
	ssoActive = false;
};

/**
 * Synthesize the MSAL-shaped account the app shell expects. Teams SSO never
 * populates the MSAL cache, so there is no real AccountInfo to read.
 */
export const resolveTeamsSsoAccount = async (): Promise<AccountInfo | null> => {
	const token = await acquireTeamsApiToken();
	if (!token) {
		return null;
	}

	const claims = decodeJwtClaims(token);
	const localAccountId = claims?.oid;
	const tenantId = claims?.tid;
	if (!(localAccountId && tenantId)) {
		return null;
	}

	const username = claims.preferred_username ?? claims.upn ?? "";

	return {
		homeAccountId: `${localAccountId}.${tenantId}`,
		environment: new URL(environment.VITE_AZURE_AUTHORITY).hostname,
		tenantId,
		username,
		localAccountId,
		name: claims.name,
	};
};
